import { Injectable } from '@angular/core'
import { FormGroup } from '@angular/forms'
import { Feature } from 'ol'
import { Polygon } from 'ol/geom'
import { toLonLat } from 'ol/proj'
import VectorSource from 'ol/source/Vector'
import { BehaviorSubject, combineLatest, debounceTime, firstValueFrom, startWith, Subscription } from 'rxjs'
import { ParametreService } from 'src/app/parametre/data-access/http/parametre.service'
import { SunleonService } from 'src/app/sunleon/data-access/http/sunleon.service'
import { GeomapDomainService } from '../domain/geomap.domain'
import {
    CalepinageInput,
    CalepinageParameters,
    OrientationTerrasse,
    PanneauOutput,
    Point,
    TypeGammePanneau,
} from '../model/calepinage.types'
import { ToolEnum } from '../model/tool.enum'
import { Zone } from '../model/zone'
import { CalepinageAlgo } from '../utils/calepinage-algo'
import { CalepinageMapping } from '../utils/calepinage-mapping'

interface ZoneFormContext {
    typePose: string | null
    typeToiture: string | null
    inclinaison: number | null
    typeVehiculeParking: string | null
    typePlaceParking: string | null
    orientation: OrientationTerrasse | null
}

interface ZoneFormFields {
    typePose: string
    typeToiture: string
    inclinaison: string
    typeVehiculeParking: string
    typePlaceParking: string
    orientation: string
}

const CODES_PARAMETRES_CALEPINAGE = [
    'LARGEUR_PANNEAU_CRE',
    'LONGUEUR_PANNEAU_CRE',
    'LARGEUR_PANNEAU_FR',
    'LONGUEUR_PANNEAU_FR',
    'PUISSANCE_PANNEAU_CRE',
    'PUISSANCE_PANNEAU_FR',
    'LARGEUR_PANNEAU_NZIA',
    'LONGUEUR_PANNEAU_NZIA',
    'PUISSANCE_PANNEAU_NZIA',
    'INCLINAISON_SUD',
    'INCLINAISON_SOL',
    'DIST_LONG_PANNEAU_BACACIER',
    'DIST_LONG_PANNEAU_OMBRIERE',
    'DIST_LONG_PANNEAU_SOUDE_SUD',
    'DIST_LONG_PANNEAU_SOUDE_ESTOUEST',
    'DIST_LONG_PANNEAU_LESTE_SUD',
    'DIST_LONG_PANNEAU_LESTE_ESTOUEST',
    'DIST_LARG_PANNEAU_BACACIER',
    'DIST_LARG_PANNEAU_OMBRIERE',
    'DIST_LARG_PANNEAU_SOUDE_SUD',
    'DIST_LARG_PANNEAU_SOUDE_ESTOUEST',
    'DIST_LARG_PANNEAU_LESTE_SUD',
    'DIST_LARG_PANNEAU_LESTE_ESTOUEST',
    'DIST_LONG_PANNEAU_SOL',
    'DIST_LARG_PANNEAU_SOL',
    'NB_PANNEAUX_LARG_BLOC_PAYSAGE',
    'NB_PANNEAUX_LONG_BLOC_PAYSAGE',
    'NB_PANNEAUX_LARG_BLOC_PORTRAIT',
    'NB_PANNEAUX_LONG_BLOC_PORTRAIT',
    'DIST_ENTRE_BLOC',
    'DECALAGE_ZONE_PANNEAU',
    'DECALAGE_ZONE_SANS_PANNEAU',
    'NB_PANNEAUX_LARG_TABLE_SOLAIRE',
    'DISTANCE_ENTRE_TABLE_L2',
    'NBRE_PANNEAUX_LARGEUR_OMB_1VL',
    'NBRE_PANNEAUX_LARGEUR_OMB_2VL',
    'NBRE_PANNEAUX_LARGEUR_OMB_2VL_ESTOUEST',
]

/**
 * Types de place VL dont l'ombrière se dessine en largeur fixe (ligne épaissie en rectangle).
 * Le formulaire porte deux champs séparés : typeVehiculeParking ('VL'/'PL') et typePlaceParking.
 */
const PLACES_OMBRIERE_LARGEUR_FIXE = ['PLACE_1', 'PLACE_2', 'PLACE_1_BOIS', 'PLACE_2_BOIS', 'EST_OUEST']

@Injectable({ providedIn: 'root' })
export class CalepinageService {
    /** Valeurs de base (non surchargées) chargées du back, par code. */
    private cachedValeursBase: Map<string, number> | null = null
    /**
     * Surcharges « paramètres modulables » du scénario courant (locales au front,
     * envoyées au back à la simulation). Appliquées par-dessus les valeurs de base.
     */
    private surchargesScenario = new Map<string, number>()
    /** Incrémentée à chaque surcharge d'un paramètre calepinage → re-déclenche le calepinage et la bulle. */
    readonly parametresVersion$ = new BehaviorSubject<number>(0)
    private subscriptions = new Map<number, Subscription>()

    /**
     * Empreinte des inputs du dernier calepinage généré, par zone. Tant que les inputs
     * (surfaces, azimut, exclusions, système, orientation, mode table) ne changent pas,
     * on NE régénère PAS : les suppressions manuelles de panneaux et l'état restauré
     * d'un scénario sont ainsi préservés. Tout changement réel d'input régénère tout.
     */
    private lastFingerprints = new Map<number, string>()

    /** Gamme panneau sélectionnée (onglet Capex généralisés) — consommée par la bulle récap. */
    readonly typeGammePanneau$ = new BehaviorSubject<TypeGammePanneau>('CRE')

    /** Zones dont le calepinage est en cours de calcul (worker) — consommé par la bulle récap (spinner). */
    readonly calculating$ = new BehaviorSubject<ReadonlySet<number>>(new Set<number>())

    /**
     * Web Worker de calepinage (perf : exécute l'algo hors thread principal → ne fige plus le
     * navigateur). Créé paresseusement ; `null` = indisponible (tests, SSR, échec) → fallback synchrone.
     */
    private calepinageWorker?: Worker | null
    private workerSeq = 0
    private workerPending = new Map<number, { input: CalepinageInput; resolve: (panneaux: PanneauOutput[]) => void }>()

    setTypeGammePanneau(gamme: TypeGammePanneau): void {
        this.typeGammePanneau$.next(gamme)
    }

    constructor(
        private parametreService: ParametreService,
        private geomapDomainService: GeomapDomainService,
        sunleonService: SunleonService
    ) {
        // Surcharge d'un paramètre modulable (modale de l'onglet Paramètres modulables) :
        // si c'est un paramètre calepinage, on l'applique et on re-déclenche le dessin.
        sunleonService.overloadableParametres$.subscribe(({ code, value }: { code: string; value: unknown }) => {
            if (!CODES_PARAMETRES_CALEPINAGE.includes(code)) return
            const parsed = parseFloat(String(value))
            if (Number.isNaN(parsed)) {
                console.warn(`[calepinage] surcharge ignorée — valeur non numérique pour ${code} :`, value)
                return
            }
            this.surchargesScenario.set(code, parsed)
            this.parametresVersion$.next(this.parametresVersion$.value + 1)
        })

        // Largeur de dessin des ombrières VL (retour client 11041) : largeur = nb_modules ×
        // LONGUEUR_PANNEAU × cos(inclinaison). Dépend de la gamme (longueur du module) et des
        // surcharges de paramètres → recalcul et push au domaine carte à chaque changement.
        combineLatest([this.typeGammePanneau$, this.parametresVersion$]).subscribe(() => {
            this.pushOmbriereWidths().catch(err =>
                console.error('[calepinage] calcul des largeurs ombrière', err)
            )
        })
    }

    /**
     * Largeur de dessin d'une ombrière VL (mètres) :
     *   largeur = nb_modules × LONGUEUR_PANNEAU × cos(inclinaison) + (nb_modules − 1) × écart
     * Les panneaux sont foreshortenés (× cos, le MÊME que computeDimensions) et séparés par l'écart
     * inter-panneaux DIST_LONG_PANNEAU_OMBRIERE ; la largeur intègre ces (nb − 1) espaces pour que
     * le rectangle dessiné se remplisse d'EXACTEMENT nb panneaux, espaces compris. Pure statique pour la testabilité.
     */
    static ombriereWidthMeters(nbModules: number, longueurPanneauM: number, inclinaisonDeg: number, ecartLongueurM: number): number {
        const cosI = Math.cos((inclinaisonDeg * Math.PI) / 180)
        return nbModules * longueurPanneauM * cosI + Math.max(0, nbModules - 1) * ecartLongueurM
    }

    /** Recalcule les largeurs de dessin des ombrières VL et les pousse au service de carte. */
    private async pushOmbriereWidths(): Promise<void> {
        const p = await this.loadParameters()
        const longueurM = p.dimensionsPanneau[this.typeGammePanneau$.value].longueurM
        const incl = p.inclinaisonSysteme
        const ecart = p.distLongPanneau.OMBRIERE
        this.geomapDomainService.setOmbriereWidths({
            width1place: CalepinageService.ombriereWidthMeters(p.nbPanneauxLargeurOmb1Vl, longueurM, incl, ecart),
            width2place: CalepinageService.ombriereWidthMeters(p.nbPanneauxLargeurOmb2Vl, longueurM, incl, ecart),
            widthEstOuest: CalepinageService.ombriereWidthMeters(p.nbPanneauxLargeurOmb2VlEstOuest, longueurM, incl, ecart),
        })
    }

    /**
     * À appeler après restauration d'un scénario (panneaux redessinés depuis la sauvegarde) :
     * invalide l'empreinte mémorisée pour que le prochain déclenchement ADOPTE l'état
     * restauré au lieu de le régénérer (préserve les suppressions manuelles persistées).
     */
    notifyZoneRestored(zoneIndex: number): void {
        this.lastFingerprints.delete(zoneIndex)
    }

    /**
     * Branche l'auto-déclenchement du calepinage pour la zone donnée. Le calepinage
     * est régénéré quand un input pertinent change réellement (surface, azimut,
     * exclusions, système, orientation, mode table) — sinon l'état affiché est
     * préservé, y compris les suppressions manuelles de panneaux. Tout prérequis
     * manquant entraîne un wipe immédiat des panneaux pour éviter de laisser un
     * calepinage obsolète.
     */
    registerZoneAutoTrigger(zoneIndex: number, form: FormGroup, fields: ZoneFormFields): void {
        this.subscriptions.get(zoneIndex)?.unsubscribe()

        const measures$ = this.geomapDomainService.getAllMeasuresForZone$(zoneIndex)
        const formChanges$ = form.valueChanges.pipe(startWith(form.value))

        // typeGammePanneau$ : les dimensions du module suivent la gamme sélectionnée
        // (réunion 26/05) → un changement de gamme doit re-déclencher le calepinage.
        // parametresVersion$ : une surcharge de paramètre modulable doit redessiner aussi.
        const sub = combineLatest([measures$, formChanges$, this.typeGammePanneau$, this.parametresVersion$])
            .pipe(debounceTime(150))
            .subscribe(() => {
                const zone = this.geomapDomainService.getZone(zoneIndex)
                if (!zone) return

                const typePose = form.get(fields.typePose)?.value as string | null
                const typeToiture = form.get(fields.typeToiture)?.value as string | null
                const inclinaisonRaw = form.get(fields.inclinaison)?.value
                const inclinaison = inclinaisonRaw != null && inclinaisonRaw !== '' ? Number(inclinaisonRaw) : null
                const typeVehiculeParking = form.get(fields.typeVehiculeParking)?.value as string | null
                const typePlaceParking = form.get(fields.typePlaceParking)?.value as string | null
                const orientation = form.get(fields.orientation)?.value as OrientationTerrasse | null

                if (!typePose) {
                    this.wipePanneaux(zoneIndex, zone)
                    return
                }
                // TOITURE exige typeToiture + inclinaison ; les autres types peuvent fonctionner avec des valeurs par défaut.
                if (typePose === 'TOITURE' && (!typeToiture || inclinaison == null)) {
                    if (zone.panneauLayer.getSource().getFeatures().length > 0) {
                        console.debug(`[calepinage] zone ${zoneIndex} — TOITURE incomplète (typeToiture=${typeToiture}, inclinaison=${inclinaison}), wipe`)
                    }
                    this.wipePanneaux(zoneIndex, zone)
                    return
                }
                if (typePose === 'OMBRIERE' && !typeVehiculeParking) {
                    if (zone.panneauLayer.getSource().getFeatures().length > 0) {
                        console.debug(`[calepinage] zone ${zoneIndex} — OMBRIERE sans typeVehiculeParking, wipe`)
                    }
                    this.wipePanneaux(zoneIndex, zone)
                    return
                }

                this.generateAndDraw(zone, { typePose, typeToiture, inclinaison, typeVehiculeParking, typePlaceParking, orientation }, zoneIndex).catch(err =>
                    console.error('Erreur génération calepinage zone ' + zoneIndex, err)
                )
            })

        this.subscriptions.set(zoneIndex, sub)
    }

    /**
     * Charge les paramètres calepinage. Les valeurs de BASE (back) sont mises en cache
     * après le premier appel ; les surcharges « paramètres modulables » du scénario
     * courant sont appliquées par-dessus à chaque appel.
     */
    async loadParameters(): Promise<CalepinageParameters> {
        if (!this.cachedValeursBase) {
            const dtos = await Promise.all(
                CODES_PARAMETRES_CALEPINAGE.map(c => firstValueFrom(this.parametreService.getParametreByCode(c)))
            )
            this.cachedValeursBase = new Map(dtos.map(d => [d.code, parseFloat(d?.valeur ?? '0')]))
        }
        const base = this.cachedValeursBase
        const num = (code: string): number => this.surchargesScenario.get(code) ?? base.get(code) ?? 0

        return {
            // Scalaires résolus pour la gamme courante juste avant chaque génération
            // (voir generateAndDraw) — valeurs FR par défaut ici.
            largeurPanneau: num('LARGEUR_PANNEAU_FR'),
            longueurPanneau: num('LONGUEUR_PANNEAU_FR'),
            dimensionsPanneau: {
                CRE: { largeurM: num('LARGEUR_PANNEAU_CRE'), longueurM: num('LONGUEUR_PANNEAU_CRE') },
                FR: { largeurM: num('LARGEUR_PANNEAU_FR'), longueurM: num('LONGUEUR_PANNEAU_FR') },
                NZIA: { largeurM: num('LARGEUR_PANNEAU_NZIA'), longueurM: num('LONGUEUR_PANNEAU_NZIA') },
            },
            puissancePanneauWc: {
                CRE: num('PUISSANCE_PANNEAU_CRE'),
                FR: num('PUISSANCE_PANNEAU_FR'),
                NZIA: num('PUISSANCE_PANNEAU_NZIA'),
            },
            inclinaisonSysteme: num('INCLINAISON_SUD'),
            inclinaisonSol: num('INCLINAISON_SOL'),
            distLongPanneau: {
                BAC_ACIER: num('DIST_LONG_PANNEAU_BACACIER'),
                OMBRIERE: num('DIST_LONG_PANNEAU_OMBRIERE'),
                SOUDE_SUD: num('DIST_LONG_PANNEAU_SOUDE_SUD'),
                SOUDE_ESTOUEST: num('DIST_LONG_PANNEAU_SOUDE_ESTOUEST'),
                LESTE_SUD: num('DIST_LONG_PANNEAU_LESTE_SUD'),
                LESTE_ESTOUEST: num('DIST_LONG_PANNEAU_LESTE_ESTOUEST'),
            },
            distLargPanneau: {
                BAC_ACIER: num('DIST_LARG_PANNEAU_BACACIER'),
                OMBRIERE: num('DIST_LARG_PANNEAU_OMBRIERE'),
                SOUDE_SUD: num('DIST_LARG_PANNEAU_SOUDE_SUD'),
                SOUDE_ESTOUEST: num('DIST_LARG_PANNEAU_SOUDE_ESTOUEST'),
                LESTE_SUD: num('DIST_LARG_PANNEAU_LESTE_SUD'),
                LESTE_ESTOUEST: num('DIST_LARG_PANNEAU_LESTE_ESTOUEST'),
            },
            distLongPanneauSol: num('DIST_LONG_PANNEAU_SOL'),
            distLargPanneauSol: num('DIST_LARG_PANNEAU_SOL'),
            nbPanneauxLargBlocPaysage: num('NB_PANNEAUX_LARG_BLOC_PAYSAGE'),
            nbPanneauxLongBlocPaysage: num('NB_PANNEAUX_LONG_BLOC_PAYSAGE'),
            nbPanneauxLargBlocPortrait: num('NB_PANNEAUX_LARG_BLOC_PORTRAIT'),
            nbPanneauxLongBlocPortrait: num('NB_PANNEAUX_LONG_BLOC_PORTRAIT'),
            distEntreBloc: num('DIST_ENTRE_BLOC'),
            decalageZonePanneau: num('DECALAGE_ZONE_PANNEAU'),
            decalageZoneSansPanneau: num('DECALAGE_ZONE_SANS_PANNEAU'),
            nbPanneauxLargTable: num('NB_PANNEAUX_LARG_TABLE_SOLAIRE'),
            distanceEntreTableM: num('DISTANCE_ENTRE_TABLE_L2'),
            nbPanneauxLargeurOmb1Vl: num('NBRE_PANNEAUX_LARGEUR_OMB_1VL'),
            nbPanneauxLargeurOmb2Vl: num('NBRE_PANNEAUX_LARGEUR_OMB_2VL'),
            nbPanneauxLargeurOmb2VlEstOuest: num('NBRE_PANNEAUX_LARGEUR_OMB_2VL_ESTOUEST'),
        }
    }

    /**
     * Génère et dessine le calepinage pour la zone donnée. Wipe l'ancien calepinage.
     * Retourne le nombre de panneaux placés.
     *
     * Les coordonnées OpenLayers sont en EPSG:3857 (Mercator) : 1 mètre réel y vaut
     * 1/cos(latitude) unités. On scale donc tous les paramètres dimensionnels par
     * ce facteur avant d'exécuter l'algo.
     */
    async generateAndDraw(zone: Zone, form: ZoneFormContext, zoneIndex: number): Promise<number> {
        const sys = CalepinageMapping.fromZone(form)
        if (!sys) {
            console.debug('[calepinage] sysIntegration null — form=', form)
            this.wipePanneaux(zoneIndex, zone)
            return 0
        }

        const features = zone.vectorLayer.getSource().getFeatures()
        const surfaceFeatures = features.filter(
            f => f.getGeometry()?.getType() === 'Polygon' && f.getGeometry()?.getProperties()['type'] === ToolEnum.Surface
        )
        const azimutFeature = features.find(
            f =>
                f.getGeometry()?.getType() === 'LineString' && f.getGeometry()?.getProperties()['type'] === ToolEnum.Azimuth
        )
        if (surfaceFeatures.length === 0) {
            console.debug('[calepinage] aucune surface tracée')
            this.wipePanneaux(zoneIndex, zone)
            return 0
        }
        // Ombrière : la flèche d'azimut est OPTIONNELLE (retour client 11041).
        //  - Largeur fixe (VL 1/2 places, bois, EST_OUEST) : rectangle = ligne épaissie → grille à
        //    90° du tracé (azimutPerpendiculaireAuTrace) ; la flèche n'est ni requise ni utilisée.
        //  - PL / portique (polygone libre) : si une flèche est tracée elle prime ; sinon la grille
        //    se cale PARALLÈLEMENT au 1er segment du polygone (azimutParalleleAuTrace).
        // Toiture / sol gardent le comportement historique : flèche d'azimut obligatoire.
        const isOmbriere = sys === 'OMBRIERE'
        const isOmbriereLargeurFixe =
            isOmbriere &&
            form.typeVehiculeParking === 'VL' &&
            PLACES_OMBRIERE_LARGEUR_FIXE.includes(form.typePlaceParking ?? '')
        if (!azimutFeature && !isOmbriere) {
            console.debug('[calepinage] azimut feature manquante')
            this.wipePanneaux(zoneIndex, zone)
            return 0
        }

        const azimutDeg = !isOmbriereLargeurFixe && azimutFeature ? CalepinageService.computeAzimuth(azimutFeature) : null
        const exclusionsCoords = zone.exclusionLayer
            .getSource()
            .getFeatures()
            .filter(f => f.getGeometry()?.getType() === 'Polygon')
            .map(f => (f.getGeometry() as Polygon).getCoordinates()[0].map(c => ({ x: c[0], y: c[1] } as Point)))

        const surfacesCoords = surfaceFeatures.map(
            f => (f.getGeometry() as Polygon).getCoordinates()[0].map(c => ({ x: c[0], y: c[1] } as Point))
        )

        // L'orientation n'a de sens que pour la toiture terrasse (SOUDE/LESTE) ; l'algo
        // l'ignore pour BAC_ACIER/OMBRIERE.
        const orientation = sys === 'SOUDE' || sys === 'LESTE' ? form.orientation ?? 'SUD' : null

        // Centrale au sol (ticket 11037) : le SOL mappe sur LESTE comme système, mais doit être
        // dessiné en « tables » (l1/L2) et non en blocs. On distingue donc le SOL d'une toiture
        // terrasse lestée via le typePose pour activer le mode table dans l'algo.
        const tableMode = form.typePose === 'SOL'

        const gamme = this.typeGammePanneau$.value
        const parametresVersion = this.parametresVersion$.value

        // Préservation des suppressions manuelles : si les inputs du calepinage n'ont pas
        // changé depuis la dernière génération, on garde l'état affiché tel quel.
        const fingerprint = JSON.stringify({ surfacesCoords, azimutDeg, exclusionsCoords, sys, orientation, tableMode, gamme, parametresVersion })
        const previous = this.lastFingerprints.get(zoneIndex)
        const panneauSource = zone.panneauLayer.getSource()
        if (previous === fingerprint) {
            return panneauSource.getFeatures().length
        }
        // Panneaux présents sans génération préalable dans cette session : ils viennent de la
        // restauration d'un scénario sauvegardé — on adopte cet état au lieu de le régénérer.
        if (previous === undefined && panneauSource.getFeatures().length > 0) {
            this.lastFingerprints.set(zoneIndex, fingerprint)
            return panneauSource.getFeatures().length
        }

        // Spinner « calcul en cours » + exécution hors thread principal (Web Worker) : le calcul
        // ne fige plus le navigateur. finally garantit la levée du spinner même en cas d'erreur.
        this.setCalculating(zoneIndex, true)
        try {
            const loaded = await this.loadParameters()
            // Le dessin suit la gamme de panneau sélectionnée (dimensions du module).
            const baseParameters: CalepinageParameters = {
                ...loaded,
                largeurPanneau: loaded.dimensionsPanneau[gamme].largeurM,
                longueurPanneau: loaded.dimensionsPanneau[gamme].longueurM,
            }

            // On exécute le calepinage sur chaque surface indépendamment, partageant le même
            // azimut et les mêmes exclusions (l'algo filtre naturellement les exclusions hors zone).
            // Ombrière : chaque rectangle a sa propre orientation, déduite de son tracé.
            const allPanneaux: PanneauOutput[] = []
            for (const zoneCoords of surfacesCoords) {
                const lat = CalepinageService.getCentroidLatitude(zoneCoords)
                const scale = 1 / Math.cos((lat * Math.PI) / 180)
                const parameters = CalepinageService.scaleParameters(baseParameters, scale)
                // Largeur fixe → 90° du tracé ; flèche dessinée → elle prime ; sinon (ombrière PL/portique
                // sans flèche) → parallèle au 1er segment du polygone tracé.
                const azimutSurface = isOmbriereLargeurFixe
                    ? CalepinageService.azimutPerpendiculaireAuTrace(zoneCoords)
                    : azimutDeg ?? CalepinageService.azimutParalleleAuTrace(zoneCoords)

                const input: CalepinageInput = {
                    zonePolygon: zoneCoords,
                    exclusionPolygons: exclusionsCoords,
                    azimutDeg: azimutSurface,
                    sysIntegration: sys,
                    orientation,
                    parameters,
                    tableMode,
                    ombriereLargeurFixe: isOmbriereLargeurFixe,
                }

                const panneaux = await this.generatePanneaux(input)
                allPanneaux.push(...panneaux)
            }

            const azimutLog = azimutDeg == null ? 'auto (par ombrière)' : `${azimutDeg.toFixed(1)}°`
            console.debug('[calepinage] surfaces=', surfacesCoords.length, '| sys=', sys, '| azimut=', azimutLog, '| panneaux placés=', allPanneaux.length)
            CalepinageService.drawPanneaux(zone.panneauLayer.getSource(), allPanneaux)
            this.lastFingerprints.set(zoneIndex, fingerprint)
            return allPanneaux.length
        } finally {
            this.setCalculating(zoneIndex, false)
        }
    }

    private wipePanneaux(zoneIndex: number, zone: Zone): void {
        zone.panneauLayer.getSource().clear()
        this.lastFingerprints.delete(zoneIndex)
    }

    private setCalculating(zoneIndex: number, on: boolean): void {
        const next = new Set(this.calculating$.value)
        if (on) next.add(zoneIndex)
        else next.delete(zoneIndex)
        this.calculating$.next(next)
    }

    /**
     * Exécute le calepinage dans un Web Worker (hors thread principal → ne fige plus le navigateur).
     * Fallback SYNCHRONE si le worker est indisponible (tests, SSR) ou en erreur : RÉSULTAT IDENTIQUE
     * (même algorithme), seul le lieu d'exécution change.
     */
    private generatePanneaux(input: CalepinageInput): Promise<PanneauOutput[]> {
        const worker = this.getCalepinageWorker()
        if (!worker) {
            return Promise.resolve(CalepinageAlgo.generate(input))
        }
        const id = ++this.workerSeq
        return new Promise<PanneauOutput[]>(resolve => {
            this.workerPending.set(id, { input, resolve })
            worker.postMessage({ id, input })
        })
    }

    private getCalepinageWorker(): Worker | null {
        if (this.calepinageWorker === undefined) {
            try {
                this.calepinageWorker =
                    typeof Worker !== 'undefined'
                        ? new Worker(new URL('../utils/calepinage.worker', import.meta.url))
                        : null
                if (this.calepinageWorker) {
                    this.calepinageWorker.onmessage = ({ data }: MessageEvent<{ id: number; panneaux: PanneauOutput[] }>) => {
                        const pending = this.workerPending.get(data.id)
                        if (pending) {
                            this.workerPending.delete(data.id)
                            pending.resolve(data.panneaux)
                        }
                    }
                    // En cas d'erreur worker : bascule définitive en synchrone et résolution des calculs
                    // en attente via le fallback (jamais de promesse pendante → pas de spinner bloqué).
                    this.calepinageWorker.onerror = () => {
                        this.calepinageWorker = null
                        this.workerPending.forEach(({ input, resolve }) => resolve(CalepinageAlgo.generate(input)))
                        this.workerPending.clear()
                    }
                }
            } catch {
                this.calepinageWorker = null
            }
            console.debug(
                '[calepinage] worker',
                this.calepinageWorker
                    ? 'actif (calcul hors thread principal)'
                    : 'indisponible → fallback synchrone (calcul sur le thread principal)'
            )
        }
        return this.calepinageWorker
    }

    private static getCentroidLatitude(zoneCoords3857: Point[]): number {
        const cx = zoneCoords3857.reduce((s, p) => s + p.x, 0) / zoneCoords3857.length
        const cy = zoneCoords3857.reduce((s, p) => s + p.y, 0) / zoneCoords3857.length
        const [, lat] = toLonLat([cx, cy], 'EPSG:3857')
        return lat
    }

    private static scaleParameters(p: CalepinageParameters, k: number): CalepinageParameters {
        const scaleRecord = (r: CalepinageParameters['distLongPanneau']) => ({
            BAC_ACIER: r.BAC_ACIER * k,
            OMBRIERE: r.OMBRIERE * k,
            SOUDE_SUD: r.SOUDE_SUD * k,
            SOUDE_ESTOUEST: r.SOUDE_ESTOUEST * k,
            LESTE_SUD: r.LESTE_SUD * k,
            LESTE_ESTOUEST: r.LESTE_ESTOUEST * k,
        })
        return {
            ...p,
            largeurPanneau: p.largeurPanneau * k,
            longueurPanneau: p.longueurPanneau * k,
            distLongPanneau: scaleRecord(p.distLongPanneau),
            distLargPanneau: scaleRecord(p.distLargPanneau),
            distLongPanneauSol: p.distLongPanneauSol * k,
            distLargPanneauSol: p.distLargPanneauSol * k,
            distEntreBloc: p.distEntreBloc * k,
            decalageZonePanneau: p.decalageZonePanneau * k,
            decalageZoneSansPanneau: p.decalageZoneSansPanneau * k,
            // nbPanneauxLargTable est un compte de modules — pas de scaling Mercator.
            distanceEntreTableM: p.distanceEntreTableM * k,
        }
    }

    private static computeAzimuth(azimutFeature: Feature): number {
        const coords = (azimutFeature.getGeometry() as any).getCoordinates() as number[][]
        if (coords.length < 2) return 0
        const [x1, y1] = coords[0]
        const [x2, y2] = coords[coords.length - 1]
        const dx = x2 - x1
        const dy = y2 - y1
        // azimut géographique : 0° = nord, sens horaire
        const angleRad = Math.atan2(dx, dy)
        const deg = (angleRad * 180) / Math.PI
        return (deg + 360) % 360
    }

    /**
     * Orientation de la grille d'une ombrière, déduite de sa géométrie : le rectangle est
     * construit en épaississant la ligne tracée (largeur fixe), son 1er segment EST le
     * tracé initial. La grille se cale à 90° de ce tracé (les rangées suivent l'ombrière).
     * Publique statique pure pour la testabilité.
     */
    static azimutPerpendiculaireAuTrace(coords: Point[]): number {
        if (coords.length < 2) return 0
        const dx = coords[1].x - coords[0].x
        const dy = coords[1].y - coords[0].y
        const azimutTrace = (Math.atan2(dx, dy) * 180) / Math.PI
        return (azimutTrace + 90 + 360) % 360
    }

    /**
     * Orientation de la grille d'une ombrière polygone libre (PL / portique) sans flèche d'azimut :
     * la grille se cale PARALLÈLEMENT au 1er segment du polygone tracé (retour client 11041).
     * Pure statique pour la testabilité.
     */
    static azimutParalleleAuTrace(coords: Point[]): number {
        if (coords.length < 2) return 0
        const dx = coords[1].x - coords[0].x
        const dy = coords[1].y - coords[0].y
        return ((Math.atan2(dx, dy) * 180) / Math.PI + 360) % 360
    }

    private static drawPanneaux(source: VectorSource, panneaux: PanneauOutput[]): void {
        // Perf : on construit toutes les features PUIS on les ajoute EN LOT (addFeatures) plutôt
        // qu'une par une (addFeature). Un ajout unitaire émet un événement par feature → re-rendu
        // OpenLayers + recalcul des stats de la bulle récap (getArea sur tous les panneaux) à chaque
        // feature, soit O(N²) sur le thread principal pour une grosse zone. En lot : un seul événement.
        const features = panneaux.map(p => {
            const ring = [...p.coordinates.map(c => [c.x, c.y]), [p.coordinates[0].x, p.coordinates[0].y]]
            const feature = new Feature({ geometry: new Polygon([ring]) })
            feature.setId(p.id)
            feature.setProperties({ type: ToolEnum.Panneau, blocId: p.blocId, id: p.id })
            return feature
        })
        source.clear()
        source.addFeatures(features)
    }

    /** Supprime un panneau par son id. */
    deletePanneau(zone: Zone, id: string): void {
        const source = zone.panneauLayer.getSource()
        const feature = source.getFeatureById(id)
        if (feature && !Array.isArray(feature)) source.removeFeature(feature as Feature)
    }
}
